import React, { useEffect } from 'react'
import { Route, Outlet } from 'react-router'
import { Routes, Navigate } from 'react-router-dom'
import Login from 'components/Login/Login'
import { PrivateRoute } from 'hoc/PrivateRoute'
import withSuspense from 'hoc/withSuspense'

import AwsCallback from 'pages/AwsCallback/AwsCallback'
import { useDispatch, useSelector } from 'react-redux'
import { useRefreshToken } from 'hooks/use-init'
import { useLocalStorage } from 'hooks/use-local-storage'
import Preloader from 'components/Preloader/Preloader'
import Fallback from 'components/Fallback/Fallback'
import { useExchangesApi } from 'pages/Exchanges/hooks'
import { useNetworksApi } from 'pages/Networks/hooks'
import { settingsActions } from 'redux/reducers/settings-reducer'
import { useIsMobile } from 'hooks/use-is-mobile'

// Route-level code splitting.
//
// Every page used to be a static import, so the LOGIN SCREEN downloaded the entire
// application — a 2.1 MB main bundle — before anyone had authenticated. withSuspense
// already existed in hoc/ and was used for exactly one route (BrokersTable).
//
// Login and AwsCallback stay eager on purpose: they are the first paint and the OAuth
// handoff, and a spinner there would be a regression, not a saving.
const lazyPage = (loader, named) =>
  withSuspense(React.lazy(named ? () => loader().then(m => ({ default: m[named] })) : loader))

const Dashboard = lazyPage(() => import('pages/Dashboard'), 'Dashboard')
const MobileDashboard = lazyPage(() => import('pages/Dashboard/mobile'), 'MobileDashboard')
const TopDeals = lazyPage(() => import('pages/TopDeals/TopDeals'))
const BotHistory = lazyPage(() => import('pages/BotHistory/BotHistory'))
const BotSignals = lazyPage(() => import('pages/BotSignals/BotSignals'))
const BotOpportunities = lazyPage(() => import('pages/BotOpportunities/BotOpportunities'))
const Bookkeeping = lazyPage(() => import('pages/Bookkeeping/Bookkeeping'))
const Nonce = lazyPage(() => import('pages/Nonce/Nonce'))
const Config = lazyPage(() => import('pages/Config/Config'))
const Deployments = lazyPage(() => import('pages/Deployments'), 'Deployments')
const Exchanges = lazyPage(() => import('pages/Exchanges'), 'Exchanges')
const Positions = lazyPage(() => import('pages/Positions'), 'Positions')
const MarketVolatility = lazyPage(() => import('pages/MarketVolatility'), 'MarketVolatility')
const Balances = lazyPage(() => import('pages/Balances'), 'Balances')
const Networks = lazyPage(() => import('pages/Networks'), 'Networks')
const SeekerRules = lazyPage(() => import('pages/SeekerRules'), 'SeekerRules')
const DexTokens = lazyPage(() => import('pages/DexTokens'), 'DexTokens')
const DexPairs = lazyPage(() => import('pages/DexPairs'), 'DexPairs')
const Allowances = lazyPage(() => import('pages/Allowances'), 'Allowances')
const DailyProfit = lazyPage(() => import('pages/DailyProfit/DailyProfit'))
const ArchivedBots = lazyPage(() => import('pages/ArchivedBots/ArchivedBots'))
const BalanceLeveling = lazyPage(() => import('pages/BalanceLeveling/BalanceLeveling'))
const BotsList = lazyPage(() => import('pages/BotDetails/BotsList'))
const BotDetails = lazyPage(() => import('pages/BotDetails/BotDetails'))
const CexFirst = lazyPage(() => import('pages/CexFirst/CexFirst'))
const RoutePolicies = lazyPage(() => import('pages/RoutePolicies'), 'RoutePolicies')
const RoutePolicyDetail = lazyPage(() => import('pages/RoutePolicies/PolicyDetail'), 'PolicyDetail')
const NewRoutePolicy = lazyPage(() => import('pages/RoutePolicies/PolicyDetail'), 'NewRoutePolicy')
const RouteEngineRollout = lazyPage(() => import('pages/RoutePolicies/Rollout'), 'Rollout')
const RouteInsights = lazyPage(() => import('pages/RouteInsights'), 'RouteInsights')
const RouteFindings = lazyPage(() => import('pages/RouteInsights/Findings'), 'Findings')
const RouteCandidateDetail = lazyPage(() => import('pages/RouteInsights/CandidateDetail'), 'CandidateDetail')
const ClickHouse = lazyPage(() => import('pages/ClickHouse'), 'ClickHouse')
const Gallery = lazyPage(() => import('pages/Gallery/Gallery'), 'Gallery')

const BrokersTable = React.lazy(() => import('components/BrokersTable/BrokersTable'))
const SuspendedBrokersTable = withSuspense(BrokersTable)

const PreloadData = () => {
  const dispatch = useDispatch()
  const [exchanges] = useExchangesApi()
  const [networks] = useNetworksApi()

  useEffect(() => {
    if (exchanges.data && networks.data) {
      dispatch(settingsActions.setInitialized(true))
    }
  }, [exchanges.data, networks.data, dispatch])

  return null
}

const RefreshToken = () => {
  const initialized = useSelector((state) => state.settings.initialized)

  useRefreshToken()
  const [isAuth] = useLocalStorage('authenticated', false)

  if (isAuth && !initialized) {
    return <PreloadData />
  }

  if (!initialized) {
    return (
      <div style={{ height: '100vh', width: '100%' }}>
        <Preloader />
      </div>
    )
  }

  return <Outlet />
}

const RoutesComponent = () => {
  // Was `isMobile` from react-device-detect, evaluated once at module load. As a hook this
  // now re-renders on resize, so narrowing the window actually switches the dashboard.
  const isMobile = useIsMobile()

  return (
    <Routes>
      <Route path='/callback/*' element={<AwsCallback />} />
      <Route path='/token-callback/*' element={<AwsCallback />} />
      <Route path='login/*' element={<Login />} />
      <Route path='/' element={<PrivateRoute><RefreshToken /></PrivateRoute>}>
        <Route index element={isMobile ? <PrivateRoute><MobileDashboard /></PrivateRoute> : <PrivateRoute><Dashboard /></PrivateRoute>} />
        {/* Two pages, not two in-page tabs. The redirects keep every existing
            bookmark working: /settings was the nonce form, /settings/bot the editor. */}
        <Route path='settings' element={<Navigate to='/settings/nonce' replace />} />
        <Route path='settings/nonce' element={<PrivateRoute><Nonce /></PrivateRoute>} />
        <Route path='settings/config' element={<PrivateRoute><Config /></PrivateRoute>} />
        <Route path='settings/bot' element={<Navigate to='/settings/config' replace />} />
        <Route path='brokers/*' element={<PrivateRoute><SuspendedBrokersTable /></PrivateRoute>} />
        <Route path='top-deals/*' element={<PrivateRoute><TopDeals /></PrivateRoute>} />
        <Route path='bot-history/:botId' element={<PrivateRoute><BotHistory /></PrivateRoute>} />
        <Route path='bot-signals/*' element={<PrivateRoute><BotSignals /></PrivateRoute>} />
        <Route path='bot-opportunities' element={<PrivateRoute><BotOpportunities /></PrivateRoute>} />
        <Route path='daily-profit/*' element={<PrivateRoute><DailyProfit /></PrivateRoute>} />
        <Route path='bookkeeping/*' element={<PrivateRoute><Bookkeeping /></PrivateRoute>} />
        <Route path='archived-bots/*' element={<PrivateRoute><ArchivedBots /></PrivateRoute>} />
        <Route path='balance-leveling/*' element={<PrivateRoute><BalanceLeveling /></PrivateRoute>} />
        <Route path='balances' element={<PrivateRoute><Balances /></PrivateRoute>} />
        <Route path='deployments' element={<PrivateRoute><Deployments /></PrivateRoute>} />
        <Route path='exchanges' element={<PrivateRoute><Exchanges /></PrivateRoute>} />
        <Route path='positions' element={<PrivateRoute><Positions /></PrivateRoute>} />
        <Route path='networks' element={<PrivateRoute><Networks /></PrivateRoute>} />
        <Route path='seeker-rules' element={<PrivateRoute><SeekerRules /></PrivateRoute>} />
        {/* Two pages, not one screen with a divider down the middle. A token and a pair are
            edited in different sittings, and the pairs table used to be below 200 token rows.
            /dex-tokens keeps its meaning, so every existing bookmark still lands on tokens. */}
        <Route path='dex-tokens' element={<PrivateRoute><DexTokens /></PrivateRoute>} />
        <Route path='dex-pairs' element={<PrivateRoute><DexPairs /></PrivateRoute>} />
        <Route path='allowances' element={<PrivateRoute><Allowances /></PrivateRoute>} />
        <Route path='market-volatility' element={<PrivateRoute><MarketVolatility /></PrivateRoute>} />
        <Route path="bot-details/*">
          <Route index element={<PrivateRoute><BotsList /></PrivateRoute>} />
          <Route path=":botId" element={<PrivateRoute><BotDetails /></PrivateRoute>} />
        </Route>
        <Route path='cex-first' element={<PrivateRoute><CexFirst /></PrivateRoute>} />
        {/* Four routes, not one page with tabs: a policy is the unit an operator links to
            in an incident, and `/route-policies/rollout` is a different question from any
            single policy. React Router ranks the two static children above `:id`. */}
        <Route path='route-policies'>
          <Route index element={<PrivateRoute><RoutePolicies /></PrivateRoute>} />
          <Route path='new' element={<PrivateRoute><NewRoutePolicy /></PrivateRoute>} />
          <Route path='rollout' element={<PrivateRoute><RouteEngineRollout /></PrivateRoute>} />
          <Route path=':id' element={<PrivateRoute><RoutePolicyDetail /></PrivateRoute>} />
        </Route>
        {/* Insights is a separate noun from policies, not a tab on them: a policy is a rule
            someone wrote, a finding is something the engine observed, and the questions
            ("why is nothing confirming?") are asked without a policy in mind. Nesting keeps
            /route-insights/findings/:candidateId linkable, which is the whole point of a
            candidate having a page at all. */}
        <Route path='route-insights'>
          <Route index element={<PrivateRoute><RouteInsights /></PrivateRoute>} />
          <Route path='findings'>
            <Route index element={<PrivateRoute><RouteFindings /></PrivateRoute>} />
            <Route path=':candidateId' element={<PrivateRoute><RouteCandidateDetail /></PrivateRoute>} />
          </Route>
        </Route>
        {/* The store rather than what is in it. Under /route-engine/ and not alongside the
            two above, because this is the only route-engine page that is not about routes:
            it answers "is the disk full" and "is TTL running", and the prefix leaves room
            for the rest of the engine's infrastructure to land next to it. */}
        <Route path='route-engine'>
          <Route path='clickhouse' element={<PrivateRoute><ClickHouse /></PrivateRoute>} />
        </Route>
        {/* Component gallery — Phase 2's exit criterion. Deliberately not in the nav:
            it is a review surface for whoever is changing shared components, and a
            fixture for the contrast gate, not a page operators need. */}
        <Route path='gallery' element={<PrivateRoute><Gallery /></PrivateRoute>} />
      </Route>
      <Route
        path='*'
        element={
          <Fallback
            title='Page not found'
            detail='That route does not exist. It may have been renamed or removed.'
            actionLabel='Back to dashboard'
            onAction={() => { window.location.href = '/' }}
          />
        }
      />
    </Routes>
  )
}

export default RoutesComponent
